import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Mail, Phone, X } from "lucide-react";

export const DIRECT_EMAIL = "office@rplcuae.com";
export const DIRECT_PHONE = "+971 58 261 6137";
export const DIRECT_PHONE_TEL = "+971582616137";

export function ConsultPopover({ triggerTestId, buttonLabel = "Consult", buttonClass = "btn-primary" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={triggerTestId}
        className={buttonClass}
        style={buttonClass === "btn-primary" ? { padding: "0.7rem 1.1rem" } : undefined}
      >
        {buttonLabel}
        <ArrowUpRight size={14} strokeWidth={1.5} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.28, ease: [0.76, 0, 0.24, 1] }}
            data-testid="consult-popover"
            className="absolute right-0 top-full mt-3 w-[320px] bg-[#0A0A0A] border border-[#222] shadow-2xl z-50"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-7 h-7 border border-[#222] flex items-center justify-center hover:border-[#F4F1EB] transition-colors"
            >
              <X size={11} strokeWidth={1.25} />
            </button>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-2 h-2 bg-[#C84C35]" />
                <span className="chapter-num">Direct</span>
              </div>
              <a
                href={`mailto:${DIRECT_EMAIL}`}
                data-testid="consult-email"
                className="flex items-start gap-3 py-3 border-b border-[#141414] hover:bg-[#141414] transition-colors -mx-2 px-2"
              >
                <Mail size={14} strokeWidth={1.25} className="text-[#C84C35] mt-1 shrink-0" />
                <div className="min-w-0">
                  <div className="chapter-num text-[#66635C]">Email</div>
                  <div className="text-[#F4F1EB] text-sm mt-1 break-all">{DIRECT_EMAIL}</div>
                </div>
              </a>
              <a
                href={`tel:${DIRECT_PHONE_TEL}`}
                data-testid="consult-phone"
                className="flex items-start gap-3 py-3 border-b border-[#141414] hover:bg-[#141414] transition-colors -mx-2 px-2"
              >
                <Phone size={14} strokeWidth={1.25} className="text-[#C84C35] mt-1 shrink-0" />
                <div>
                  <div className="chapter-num text-[#66635C]">Phone</div>
                  <div className="text-[#F4F1EB] text-sm mt-1">{DIRECT_PHONE}</div>
                </div>
              </a>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="consult-form-link"
                className="mt-4 btn-primary w-full justify-center"
                style={{ padding: "0.75rem 1rem" }}
              >
                Send a message
                <ArrowUpRight size={13} strokeWidth={1.5} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
